import type { Session } from "@mux/sdk/v2/client";
import { retry } from "@/utils/retry";

type SessionModelSelection = {
  agent: string;
  providerID: string;
  modelID: string;
  variant: string;
};

type ModelClient = {
  session: {
    update(input: {
      sessionID: string;
      agent: string;
      model: { providerID: string; modelID: string; variant: string };
    }): Promise<{ data?: Session }>;
  };
};

const selectionFor = (session: Session): SessionModelSelection => ({
  agent: session.agent ?? "build",
  providerID: session.providerID ?? "",
  modelID: session.modelID ?? "",
  variant: session.variant ?? "",
});

const isSessionUpdateResponse = (value: unknown, sessionID: string): value is Session => {
  if (!value || typeof value !== "object") return false;
  const session = value as Partial<Session>;
  return (
    session.id === sessionID &&
    typeof session.providerID === "string" &&
    session.providerID.length > 0 &&
    typeof session.modelID === "string" &&
    session.modelID.length > 0
  );
};

const sameSelection = (
  a: SessionModelSelection | undefined,
  b: SessionModelSelection,
) =>
  !!a &&
  a.agent === b.agent &&
  a.providerID === b.providerID &&
  a.modelID === b.modelID &&
  a.variant === b.variant;

export function createSessionModelUpdater(input: {
  clientFor: (directory: string) => ModelClient;
  getSession: (sessionID: string) => Session | undefined;
  /** True for a server-owned session; otherwise resolves false if the provisional session is discarded. */
  ready: (sessionID: string) => true | Promise<boolean>;
  updateLocal: (
    sessionID: string,
    selection: SessionModelSelection,
    expected?: SessionModelSelection,
  ) => void;
}) {
  const updates = new Map<string, Promise<void>>();
  const desiredSelections = new Map<string, SessionModelSelection>();
  const confirmedSelections = new Map<string, SessionModelSelection>();

  let disposed = false;
  const update = (
    directory: string,
    sessionID: string,
    selection: Omit<SessionModelSelection, "variant"> & { variant?: string },
  ) => {
    if (disposed) return Promise.resolve();
    const key = `${directory}\n${sessionID}`;
    const desired = { ...selection, variant: selection.variant ?? "" };
    const current = input.getSession(sessionID);
    const pending = updates.get(key);
    if (pending && sameSelection(desiredSelections.get(key), desired)) return pending;
    if (!pending && current && sameSelection(selectionFor(current), desired))
      return Promise.resolve();

    if (!pending && current) confirmedSelections.set(key, selectionFor(current));
    input.updateLocal(sessionID, desired);

    const previous = pending ?? Promise.resolve();
    let next!: Promise<void>;
    next = previous
      .catch(() => undefined)
      .then(async () => {
        const ready = input.ready(sessionID);
        if (ready !== true && !(await ready)) return;
        const result = await retry(() =>
          input.clientFor(directory).session.update({
            sessionID,
            agent: desired.agent,
            model: {
              providerID: desired.providerID,
              modelID: desired.modelID,
              variant: desired.variant,
            },
          }),
        );
        if (disposed) return;
        if (!isSessionUpdateResponse(result.data, sessionID)) {
          throw new Error("Session model update returned an invalid session");
        }
        const confirmed = selectionFor(result.data);
        confirmedSelections.set(key, confirmed);
        if (updates.get(key) !== next) return;
        desiredSelections.set(key, confirmed);
        input.updateLocal(sessionID, confirmed, desired);
      })
      .catch((error) => {
        if (updates.get(key) === next) {
          const confirmed = confirmedSelections.get(key);
          if (confirmed) input.updateLocal(sessionID, confirmed, desired);
        }
        throw error;
      });

    updates.set(key, next);
    desiredSelections.set(key, desired);
    const cleanup = () => {
      if (updates.get(key) !== next) return;
      updates.delete(key);
      desiredSelections.delete(key);
      confirmedSelections.delete(key);
    };
    void next.then(cleanup, cleanup);
    return next;
  };

  return Object.assign(update, {
    preservePendingSelection(session: Session) {
      for (const [key, desired] of desiredSelections) {
        if (!key.endsWith(`\n${session.id}`)) continue;
        return { ...session, ...desired };
      }
      return session;
    },
    dispose() {
      disposed = true;
      updates.clear();
      desiredSelections.clear();
      confirmedSelections.clear();
    },
  });
}
